import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRightOutlined,
    CheckCircleFilled,
    CheckOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    LoadingOutlined,
    LockOutlined,
    PaperClipOutlined,
    SendOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { Tooltip, message } from "../../../design-system";
import { AppHeader } from "../../layout/AppHeader";
import { useUserState } from "../../../common/UserContext";
import { getInitials } from "../../../common/components/NameAvatar";
import { BrandMark } from "../components/MessageBubble";
import { DEMO_PPA, daysToOfferExpiry, fmtM } from "../../demo/demoPpa";
import {
    FINDINGS,
    LOCKED_SECTIONS,
    RENEWAL_ACK_MS,
    RENEWAL_DOCUMENTS,
    RENEWAL_EXCHANGES,
    RENEWAL_OPENER,
    RENEWAL_PROJECT_NAME,
    RENEWAL_READ_BACK,
} from "./renewalScript";
import classes from "./RenewalIntakeDemo.module.less";

// AWS PPA intake demo — a self-contained, dark-themed wizard following the
// guided intake's shape, but with the flow this deal actually needs: step 1 is
// the preliminary report (content mirrors preliminary-report-sample.html:
// verdict, what's-on-the-table band, findings, and the locked full-analysis
// skeleton — the sample's extracted-terms block is deliberately dropped), and
// the user decides whether to start the re-negotiation or accept as proposed.
// Step 2 shows the documents that came with the deal (plus "anything else to
// add?"); step 3 is the discussion bot collecting
// the five inputs the findings flagged, on the chat-demo click-to-play
// mechanics. Everything is scripted locally — no backend calls.

const deal = DEMO_PPA;

type Step = "report" | "documents" | "conversation" | "done";

const STEP_LABELS: Array<{ key: Step; label: string }> = [
    { key: "report", label: "Preliminary report" },
    { key: "documents", label: "Documents" },
    { key: "conversation", label: "Context" },
];

// ---------------------------------------------------------------------------
// Provenance chips — dark variants of the landing page's palette.
// ---------------------------------------------------------------------------
type Provenance = "projection";

const PROVENANCE: Record<Provenance, { label: string; tip: string; className: string }> = {
    projection: {
        label: "benchmark projection",
        tip:
            "Projected from benchmark data alone: AWS published pricing, " +
            "anonymized $50M+ enterprise cloud agreements (2024-2026 corpus), " +
            "and advisory benchmarks. Organizational context narrows it.",
        className: "chipDerived",
    },
};

const Chip: React.FC<{ kind: Provenance }> = ({ kind }) => {
    const p = PROVENANCE[kind];
    return (
        <Tooltip title={p.tip}>
            <span className={`${classes.chip} ${classes[p.className]}`}>{p.label}</span>
        </Tooltip>
    );
};

// ---------------------------------------------------------------------------
// Local models
// ---------------------------------------------------------------------------
interface DocRow {
    id: number;
    filename: string;
    size: string;
    type: string;
    state: "reading" | "ready";
}

// A bot chat row — the real intake's shape: brand-mark avatar, transparent body.
const BotMsg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className={`${classes.msg} ${classes.msgBot}`}>
        <div className={`${classes.msgAvatar} ${classes.msgAvatarBot}`}>
            <BrandMark />
        </div>
        <div className={classes.msgBody}>{children}</div>
    </div>
);

// A document row — the real intake's shape: status check, name + size, type pill.
const DocRowView: React.FC<{ doc: DocRow }> = ({ doc }) => (
    <div className={classes.docRow}>
        <div className={classes.docCheck}>
            {doc.state === "reading" ? (
                <LoadingOutlined className={classes.docSpinner} />
            ) : (
                <CheckCircleFilled className={classes.docReadyIcon} />
            )}
        </div>
        <div className={classes.docBody}>
            <div className={classes.docLabelLine}>
                <span
                    className={`${classes.docLabel} ${
                        doc.state === "reading" ? classes.docLabelPending : ""
                    }`}
                >
                    {doc.filename}
                </span>
                {doc.state === "ready" && (
                    <span className={classes.docSize}>{doc.size}</span>
                )}
            </div>
            {doc.state === "ready" ? (
                <div className={classes.docTypes}>
                    <span className={classes.docTypePill}>{doc.type.toUpperCase()}</span>
                </div>
            ) : (
                <div className={classes.docStatus}>Reading content…</div>
            )}
        </div>
    </div>
);

interface Msg {
    id: number;
    role: "user" | "bot";
    text: string;
    typing?: boolean;
}

// The range bar's geometry — the preliminary band on a $0..scaleMax track.
const RANGE_LEFT_PCT = (deal.prelimLow / deal.prelimScaleMax) * 100;
const RANGE_WIDTH_PCT = ((deal.prelimHigh - deal.prelimLow) / deal.prelimScaleMax) * 100;
const RANGE_TICKS = [0, 1, 2, 3].map((i) => {
    const n = (deal.prelimScaleMax * i) / 3;
    return n === 0 ? "$0" : fmtM(n);
});

export const RenewalIntakeDemo: React.FC = () => {
    const navigate = useNavigate();
    const { me } = useUserState();

    const [step, setStep] = useState<Step>("report");

    // Script timers — cleared on unmount so a closed wizard can't dispatch late.
    const timersRef = useRef<number[]>([]);
    const later = (fn: () => void, ms: number) => {
        timersRef.current.push(window.setTimeout(fn, ms));
    };
    useEffect(() => () => timersRef.current.forEach(clearTimeout), []);
    const idRef = useRef(0);
    const nextId = () => ++idRef.current;

    // ── Step 1 · Preliminary report ────────────────────────────────────────
    // Prefill-loading: a beat of shimmer before the report lands.
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const t = window.setTimeout(() => setLoading(false), 650);
        return () => window.clearTimeout(t);
    }, []);

    const days = useMemo(() => daysToOfferExpiry(), []);
    // "Proceed as proposed" flips the decision row into an inline warning
    // confirm before anything closes.
    const [declining, setDeclining] = useState(false);

    // ── Step 2 · Documents ─────────────────────────────────────────────────
    const [docRows, setDocRows] = useState<DocRow[]>([]);
    const [ackTyping, setAckTyping] = useState(false);
    const [readBack, setReadBack] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const startDocuments = () => {
        setStep("documents");
        const rows = RENEWAL_DOCUMENTS.map((d) => ({
            id: nextId(),
            filename: d.filename,
            size: d.size,
            type: d.type,
            state: "reading" as const,
        }));
        setDocRows(rows);
        RENEWAL_DOCUMENTS.forEach((d, i) =>
            later(
                () =>
                    setDocRows((rs) =>
                        rs.map((r) => (r.id === rows[i].id ? { ...r, state: "ready" } : r))
                    ),
                d.parseMs
            )
        );
        const settledMs = Math.max(...RENEWAL_DOCUMENTS.map((d) => d.parseMs));
        later(() => setAckTyping(true), settledMs);
        later(() => {
            setAckTyping(false);
            setReadBack(RENEWAL_READ_BACK);
        }, settledMs + RENEWAL_ACK_MS);
    };

    // "Anything else to add?" — extra files exist only locally: a row appears
    // reading and turns Ready a beat later. Nothing is uploaded anywhere.
    const handleAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        files.forEach((f) => {
            const id = nextId();
            setDocRows((rs) => [
                ...rs,
                {
                    id,
                    filename: f.name,
                    size: `${Math.max(1, Math.round(f.size / 1024))} KB`,
                    type: "Supporting doc",
                    state: "reading",
                },
            ]);
            later(
                () =>
                    setDocRows((rs) =>
                        rs.map((r) => (r.id === id ? { ...r, state: "ready" } : r))
                    ),
                900
            );
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ── Step 3 · Conversation (the five questions) ─────────────────────────
    const [messages, setMessages] = useState<Msg[]>([]);
    const [turn, setTurn] = useState(0);
    const [sending, setSending] = useState(false);
    const [draft, setDraft] = useState("");
    const transcriptRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = transcriptRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages]);

    const startConversation = () => {
        setStep("conversation");
        const typingId = nextId();
        setMessages([{ id: typingId, role: "bot", text: "", typing: true }]);
        setSending(true);
        later(() => {
            setMessages([{ id: typingId, role: "bot", text: RENEWAL_OPENER }]);
            setSending(false);
        }, 1400);
    };

    // A send click plays the next scripted exchange — typed text is ignored,
    // and an empty send is fine (same mechanics as the guided intake chat demo).
    const send = () => {
        const exchange = RENEWAL_EXCHANGES[turn];
        if (!exchange || sending) return;
        setSending(true);
        setDraft("");
        const typingId = nextId();
        setMessages((ms) => [
            ...ms,
            { id: nextId(), role: "user", text: exchange.user },
            { id: typingId, role: "bot", text: "", typing: true },
        ]);
        later(() => {
            setMessages((ms) =>
                ms.map((m) =>
                    m.id === typingId ? { ...m, typing: false, text: exchange.reply } : m
                )
            );
            setSending(false);
            setTurn((t) => t + 1);
        }, exchange.replyMs);
    };

    const conversationDone = turn >= RENEWAL_EXCHANGES.length && !sending;

    // ── Step 4 · Request analysis ──────────────────────────────────────────
    const [submitting, setSubmitting] = useState(false);
    const requestAnalysis = () => {
        setSubmitting(true);
        later(() => {
            setSubmitting(false);
            setStep("done");
        }, 900);
    };

    const closeWizard = () => navigate("/project");
    const proceedAsProposed = () => {
        message.success(
            "Noted. The proposal stands as-is. You can reopen this from your Slack message anytime."
        );
        closeWizard();
    };

    // ── Rendering ──────────────────────────────────────────────────────────
    const stepIndex = STEP_LABELS.findIndex((s) => s.key === step);

    const userInitials = getInitials(me.name || me.email || "user").toUpperCase();

    const coverageItems: Array<{ label: string; hint: string; done: boolean }> = [
        {
            label: "Preliminary report",
            hint: "Findings and opportunity band reviewed",
            done: true,
        },
        {
            label: "Documents",
            hint: "Executed MSA and PPA proposal",
            done: Boolean(readBack),
        },
        ...RENEWAL_EXCHANGES.map((ex, i) => ({
            label: ex.topic,
            hint: ex.topicHint,
            done: turn > i,
        })),
    ];

    const typingDots = (
        <span className={classes.typing}>
            <span />
            <span />
            <span />
        </span>
    );

    return (
        <div className={classes.page}>
            {/* The real app header — same chrome the product shows above the
                guided intake dialog; its #141e2e blends into the dark theme. */}
            <AppHeader me={me} />
            {/* Wizard (dialog) chrome */}
            <div className={classes.topBar}>
                <div className={classes.topBarInner}>
                    <div className={classes.topLeft}>
                        <span className={classes.brand}>Guided Intake</span>
                        <span className={classes.divider} />
                        <span className={classes.projectName}>
                            {step === "report"
                                ? `${deal.vendor} ${deal.dealName}`
                                : RENEWAL_PROJECT_NAME}
                        </span>
                    </div>
                    {step !== "done" && (
                        <div className={classes.steps}>
                            {STEP_LABELS.map((s, i) => (
                                <span
                                    key={s.key}
                                    className={`${classes.stepItem} ${
                                        i === stepIndex
                                            ? classes.stepActive
                                            : i < stepIndex
                                            ? classes.stepDone
                                            : ""
                                    }`}
                                >
                                    <span className={classes.stepDot}>
                                        {i < stepIndex ? <CheckOutlined /> : i + 1}
                                    </span>
                                    {s.label}
                                </span>
                            ))}
                        </div>
                    )}
                    <button
                        type="button"
                        className={classes.closeBtn}
                        aria-label="Close"
                        onClick={closeWizard}
                    >
                        <CloseOutlined />
                    </button>
                </div>
            </div>

            {/* ── Step 1 · Preliminary report ── */}
            {step === "report" &&
                (loading ? (
                    <div className={classes.confirmCol}>
                        <div className={classes.skelBar} style={{ width: "40%" }} />
                        <div className={classes.skelBar} style={{ width: "70%" }} />
                        <div className={classes.skelBlock} />
                        <div className={classes.skelBlock} />
                    </div>
                ) : (
                    <div className={classes.confirmCol}>
                        {/* Deal header — the proposal clock */}
                        <div className={classes.hero}>
                            <div>
                                <h1>{deal.vendor}</h1>
                                <span className={classes.heroTerm}>
                                    {deal.dealName} · {fmtM(deal.tcv)} · {deal.termMonths}{" "}
                                    months · {deal.termStart} - {deal.termEnd}
                                </span>
                            </div>
                            <div className={classes.heroChips}>
                                <span className={`${classes.statChip} ${classes.statAmber}`}>
                                    Proposal pricing expires {deal.offerExpiresLabel} ·{" "}
                                    {days} days
                                </span>
                            </div>
                        </div>

                        {/* Hero — posture verdict + what's on the table */}
                        <div className={classes.heroGrid}>
                            <section className={classes.card}>
                                <div className={classes.cardHead}>
                                    <h2>Preliminary read</h2>
                                </div>
                                <p className={classes.verdict}>
                                    Headline pricing is{" "}
                                    <span className={classes.verdictOk}>market-aligned</span>.
                                    The structure{" "}
                                    <span className={classes.verdictWarn}>favors AWS</span> at
                                    this commitment size.
                                </p>
                                <p className={classes.subNote}>
                                    A read on the proposal against market, not a signing
                                    decision. That decision depends on organizational context
                                    the full analysis incorporates.
                                </p>
                            </section>
                            <section className={classes.card}>
                                <div className={classes.cardHead}>
                                    <h2>What's typically on the table</h2>
                                </div>
                                <div className={classes.stakesNum}>
                                    {fmtM(deal.prelimLow)} – {fmtM(deal.prelimHigh)}
                                </div>
                                <p className={classes.subNote}>
                                    Roughly {deal.prelimPctLabel} for proposals with this
                                    profile, from benchmark data alone. Organizational context
                                    narrows the band into specific levers with defensible
                                    asks. <Chip kind="projection" />
                                </p>
                                <div className={classes.rangeTrack}>
                                    <div
                                        className={classes.rangeBand}
                                        style={{
                                            left: `${RANGE_LEFT_PCT}%`,
                                            width: `${RANGE_WIDTH_PCT}%`,
                                        }}
                                    />
                                </div>
                                <div className={classes.rangeScale}>
                                    {RANGE_TICKS.map((t) => (
                                        <span key={t}>{t}</span>
                                    ))}
                                </div>
                                <p className={classes.rangeNote}>
                                    The full analysis narrows this band and assigns it to
                                    levers.
                                </p>
                            </section>
                        </div>

                        {/* Findings */}
                        <div className={classes.sectionIntro}>
                            <h2>Findings</h2>
                            <p>
                                Four gaps versus buyer-optimized norms. Materiality depends
                                on inputs the analysis does not yet include.
                            </p>
                        </div>
                        {FINDINGS.map((f) => (
                            <section className={classes.card} key={f.title}>
                                <h3 className={classes.findingTitle}>{f.title}</h3>
                                <div className={classes.findingGrid}>
                                    <div className={classes.slot}>
                                        <div className={classes.slotLabel}>Documents show</div>
                                        <p>{f.docs}</p>
                                    </div>
                                    <div className={classes.slot}>
                                        <div className={classes.slotLabel}>
                                            Market reference
                                        </div>
                                        <p className={classes.slotMuted}>{f.market}</p>
                                    </div>
                                    <div
                                        className={`${classes.slot} ${classes.slotMaterial}`}
                                    >
                                        <div className={classes.slotLabel}>Material if</div>
                                        <p>{f.materialIf}</p>
                                    </div>
                                    <div className={classes.slot}>
                                        <div className={classes.slotLabel}>Resolved by</div>
                                        <p>
                                            <span className={classes.inputChip}>
                                                {f.resolvedBy}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </section>
                        ))}

                        {/* Full analysis skeleton */}
                        <div className={classes.sectionIntro}>
                            <h2>Full analysis</h2>
                            <p>Contents of the full analysis. Each section requires a specific input.</p>
                        </div>
                        <section className={classes.card}>
                            {LOCKED_SECTIONS.map((s) => (
                                <div className={classes.lockRow} key={s.name}>
                                    <span className={classes.lockIcon}>
                                        {s.icon === "clock" ? (
                                            <ClockCircleOutlined />
                                        ) : (
                                            <LockOutlined />
                                        )}
                                    </span>
                                    <span className={classes.lockName}>
                                        {s.name}
                                        <span>{s.sub}</span>
                                    </span>
                                    <span className={classes.lockUnlocks}>
                                        {s.partial && (
                                            <span className={classes.partialChip}>
                                                partially available now
                                            </span>
                                        )}
                                        {s.unlocks.map((u) => (
                                            <span className={classes.inputChip} key={u}>
                                                {u}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </section>

                        {/* Decide */}
                        {declining ? (
                            <section className={`${classes.card} ${classes.warnCard}`}>
                                <div className={classes.warnHead}>
                                    <WarningOutlined /> Accept as proposed?
                                </div>
                                <p className={classes.read}>
                                    Accepting as proposed locks the {deal.rampPctLabel} ramp,
                                    the conditional credits, and uncapped egress for{" "}
                                    {deal.termMonths} months, and leaves the{" "}
                                    <strong>
                                        {fmtM(deal.prelimLow)}–{fmtM(deal.prelimHigh)}
                                    </strong>{" "}
                                    typically on the table.
                                </p>
                                <div className={classes.startActions}>
                                    <button
                                        type="button"
                                        className={classes.ghostBtn}
                                        onClick={proceedAsProposed}
                                    >
                                        Accept anyway
                                    </button>
                                    <button
                                        type="button"
                                        className={classes.primaryBtn}
                                        onClick={() => setDeclining(false)}
                                    >
                                        Keep the option open
                                    </button>
                                </div>
                            </section>
                        ) : (
                            <div className={`${classes.card} ${classes.ctaRow}`}>
                                <div className={classes.ctaMeter}>
                                    <strong>
                                        Starting the analysis uses 1 of{" "}
                                        {deal.analysesRemaining}.
                                    </strong>{" "}
                                    Viewing this report uses none.
                                </div>
                                <div className={classes.ctaPrimary}>
                                    <div className={classes.startActions}>
                                        <button
                                            type="button"
                                            className={classes.ghostBtn}
                                            onClick={() => setDeclining(true)}
                                        >
                                            Accept as proposed
                                        </button>
                                        <button
                                            type="button"
                                            className={classes.primaryBtn}
                                            onClick={startDocuments}
                                        >
                                            Start re-negotiation <ArrowRightOutlined />
                                        </button>
                                    </div>
                                    <span className={classes.effortNote}>
                                        5 questions plus any supporting documents · full
                                        playbook in 3 business days
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

            {/* ── Steps 2–3 · flow + rail ── */}
            {(step === "documents" || step === "conversation") && (
                <div className={classes.split}>
                    <div className={classes.flow}>
                        {step === "documents" && (
                            <div className={classes.flowScroll}>
                                <BotMsg>
                                    <div className={classes.msgText}>
                                        These came with the deal. Reading them now.
                                    </div>
                                </BotMsg>
                                <div className={classes.docList}>
                                    {docRows.map((d) => (
                                        <DocRowView doc={d} key={d.id} />
                                    ))}
                                </div>
                                {ackTyping && <BotMsg>{typingDots}</BotMsg>}
                                {readBack && (
                                    <BotMsg>
                                        <div className={classes.msgText}>{readBack}</div>
                                    </BotMsg>
                                )}
                                <div className={classes.docActions}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={handleAttach}
                                    />
                                    <button
                                        type="button"
                                        className={classes.ghostBtn}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <PaperClipOutlined /> Add more documents
                                    </button>
                                    <button
                                        type="button"
                                        className={classes.primaryBtn}
                                        disabled={!readBack}
                                        onClick={startConversation}
                                    >
                                        Continue <ArrowRightOutlined />
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === "conversation" && (
                            <>
                                <div className={classes.flowScroll} ref={transcriptRef}>
                                    {messages.map((m) =>
                                        m.role === "bot" ? (
                                            <BotMsg key={m.id}>
                                                {m.typing ? (
                                                    typingDots
                                                ) : (
                                                    <div className={classes.msgText}>
                                                        {m.text}
                                                    </div>
                                                )}
                                            </BotMsg>
                                        ) : (
                                            <div
                                                className={`${classes.msg} ${classes.msgUser}`}
                                                key={m.id}
                                            >
                                                <div className={classes.msgAvatar}>
                                                    {userInitials}
                                                </div>
                                                <div className={classes.msgBody}>
                                                    <div className={classes.msgBubble}>
                                                        {m.text}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                                {conversationDone ? (
                                    <div className={classes.composer}>
                                        <span className={classes.startMeta}>
                                            Uses 1 of {deal.analysesRemaining} analyses · full
                                            playbook in 3 business days
                                        </span>
                                        <button
                                            type="button"
                                            className={classes.primaryBtn}
                                            disabled={submitting}
                                            onClick={requestAnalysis}
                                        >
                                            {submitting ? <LoadingOutlined /> : null} Request
                                            analysis
                                        </button>
                                    </div>
                                ) : (
                                    <div className={classes.composer}>
                                        <input
                                            className={classes.composerInput}
                                            placeholder="Click send to continue the conversation…"
                                            value={draft}
                                            onChange={(e) => setDraft(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && send()}
                                        />
                                        <button
                                            type="button"
                                            className={classes.sendBtn}
                                            aria-label="Send"
                                            disabled={sending}
                                            onClick={send}
                                        >
                                            <SendOutlined />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <aside className={classes.rail}>
                        <div className={classes.railCard}>
                            <div className={classes.sectionLabel}>Documents</div>
                            {docRows.map((d) => (
                                <DocRowView doc={d} key={d.id} />
                            ))}
                        </div>
                        <div className={classes.railCard}>
                            <div className={classes.sectionLabel}>Context</div>
                            {coverageItems.map((item) => (
                                <div
                                    className={`${classes.ctxRow} ${
                                        item.done ? classes.ctxRowDone : ""
                                    }`}
                                    key={item.label}
                                >
                                    <div className={classes.ctxCheck}>
                                        {item.done ? <CheckOutlined /> : null}
                                    </div>
                                    <div className={classes.ctxBody}>
                                        <div className={classes.ctxLabel}>{item.label}</div>
                                        <div className={classes.ctxHint}>{item.hint}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </aside>
                </div>
            )}

            {/* ── Done ── */}
            {step === "done" && (
                <div className={classes.done}>
                    <span className={classes.doneCheck}>
                        <CheckOutlined />
                    </span>
                    <h1>Full analysis in progress</h1>
                    <p>
                        The analysis starts warm: the PPA draft, your usage export, and your
                        context all attached. We'll ping you in Slack when the playbook is
                        ready, within 3 business days.
                    </p>
                    <span className={classes.doneMeta}>
                        {deal.analysesRemaining - 1} of {deal.analysesTotal} analyses remaining
                    </span>
                    {/* Demo shortcut: jump straight to the delivered playbook in the
                        production demo env instead of waiting out the 3 days. */}
                    <a className={classes.ghostBtn} href={deal.playbookUrl}>
                        Open the negotiation playbook
                    </a>
                </div>
            )}
        </div>
    );
};
